import { Injectable, signal, computed } from '@angular/core';

/**
 * Global Loading Service
 *
 * Manages application-wide loading state using Angular signals.
 * Uses request counting to properly handle concurrent API requests.
 *
 * @example
 * ```typescript
 * // Automatic usage via LoadingInterceptor (recommended)
 * // The interceptor automatically shows/hides loading for HTTP requests
 *
 * // Manual usage for non-HTTP operations
 * loadingService.show();
 * await someOperation();
 * loadingService.hide();
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  // Track the number of active requests
  private activeRequests = signal(0);
  
  // Internal loading state that respects minDisplayTime
  private _isLoading = signal(false);

  // Public readonly signal for components to consume
  readonly isLoading = this._isLoading.asReadonly();

  // Minimum display time to prevent flickering (ms)
  private minDisplayTime = 500;
  private startTime = 0;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Increment the loading counter
   * Call when starting an async operation
   */
  show(): void {
    const currentCount = this.activeRequests();
    this.activeRequests.update((count) => count + 1);

    // If this is the first request and we're not already loading
    if (currentCount === 0) {
      // Cancel any pending hide timeout
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }
      
      this._isLoading.set(true);
      this.startTime = Date.now();
    }
  }

  /**
   * Decrement the loading counter
   * Call when an async operation completes (success or error)
   */
  hide(): void {
    this.activeRequests.update((count) => Math.max(0, count - 1));

    // If no more active requests
    if (this.activeRequests() === 0) {
      const elapsed = Date.now() - this.startTime;
      const remainingTime = Math.max(0, this.minDisplayTime - elapsed);

      // If we have a pending timeout, don't set another one
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
      }

      // Delay hiding if needed to meet minimum display time
      this.hideTimeout = setTimeout(() => {
        // Double check active requests (in case a new one started during the delay)
        if (this.activeRequests() === 0) {
          this._isLoading.set(false);
          this.hideTimeout = null;
        }
      }, remainingTime);
    }
  }

  /**
   * Force reset the loading counter
   * Use sparingly - only for error recovery scenarios
   */
  reset(): void {
    this.activeRequests.set(0);
    this._isLoading.set(false);
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  /**
   * Get the current count of active requests (for debugging)
   */
  getActiveCount(): number {
    return this.activeRequests();
  }
}
